import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  DatePicker,
  Table,
  Dropdown,
  Space,
  Typography,
  Checkbox,
  Menu,
} from "antd";
import Heading from "../../components/heading";
import "./package-history.css";
import { formatToLongDate, wrappedText } from "../../components/Shared/utils";
import {
  onFilterChange,
  filterUniqueValues,
  generatePDF,
  generateExcel,
  mapKeysToColumnTitles,
  sortDataOnDate,
  getCurrentDateTimeForFilename,
  downloadFileUsingBase64,
  downloadFileUsingPreSignedURL,
  sortFilterData,
  } from "./utils";
import { packageHistoryList } from "../../services/PackageHistoryAPI/package-history-list";
import { useOpenNotification } from "../../components/Notification/notification";
import { CSVLink } from "react-csv";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  allColumnsType,
  tableDataType,
} from "../../types/PackageHistory/package-history.types";
import { DownOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  sortByTransferId,
  filterByDate,
  sortByDate,
  sortByUpdatedBy,
} from "./table-sort-filter";
import GenericDialog from "../../components/GenericDialog/generic-dialog";
import { downloadPackageHistoryReport } from "../../services/download-package-history-report";

const PackageHistory: React.FC = () => {
  const { Text } = Typography;
  const [form] = Form.useForm();
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [data, setData] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [filteredData, setFilteredData] = useState<any[]>(data);
  const [dataForExport, setDataForExport] = useState<tableDataType[]>([]);
  const [selectedData, setSelectedData] = useState<tableDataType[]>([]);
  const { openNotification, contextHolder } = useOpenNotification();
  const [tableRows, setTableRows] = useState({
    current: 1,
    pageSize: 6,
  });
  const [openZipDownloadWarning, setOpenZipDownloadWarning] = useState(false);
  type SortOrder = "ascend" | "descend" | null;
  const csvLinkRef = useRef<any>(null);

  useEffect(() => {
    packageHistoryList()
      .then((response: any) => {
        setData(response.data);
        setDataLoading(false);
      })
      .catch((error: any) => {
        openNotification("error", "Failed to load Package History data");
        setDataLoading(false);
      });
  }, [openNotification]);

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const getUniqueValues = (field: string) => {
    const isoDateRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?$/;
    return filterUniqueValues(field, filteredData, isoDateRegex);
  };

  const allColumns: allColumnsType = [
    {
      title: "Compound",
      key: "compound",
      dataIndex: "msd_compound",
      render: (text: any) => text.split("-")[0],
      sorter: (a: any, b: any) => a.msd_compound.localeCompare(b.msd_compound),
      filters: getUniqueValues("msd_compound"),
      onFilter: (value: any, record: any) => record.msd_compound.split("-")[0] === value,
    },
    {
      title: "Protocol Number",
      key: "protocolNumber",
      dataIndex: "protocol_number",
      sorter: (a: any, b: any) =>
        a.protocol_number.localeCompare(b.protocol_number),
      filters: getUniqueValues("protocol_number"),
      onFilter: (value: any, record: any) => record.protocol_number === value,
    },
    {
      title: "Transfer ID",
      key: "transferId",
      dataIndex: "transfer_id",
      filters: getUniqueValues("transfer_id"),
      render: (text: any) => wrappedText(text, 30),
      sorter: (a: any, b: any) => sortByTransferId(a, b),
      onFilter: (value: any, record: any) => record.transfer_id === value,
    },
    {
      title: "Supplier",
      key: "supplier",
      dataIndex: "supplier_org",
      sorter: (a: any, b: any) => a.supplier_org.localeCompare(b.supplier_org),
      filters: getUniqueValues("supplier_org"),
      onFilter: (value: any, record: any) => record.supplier_org === value,
    },
    {
      title: "DTS Version",
      key: "DTS Version",
      dataIndex: "dts_version",
      sorter: (a: any, b: any) => a.dts_version.localeCompare(b.dts_version),
      filters: getUniqueValues("dts_version"),
      onFilter: (value: any, record: any) => record.dts_version === value,
    },
    {
      title: "Receipt Date",
      key: "receiptDate",
      dataIndex: "receipt_date_time",
      render: (text: any) =>
        text !== null && text !== "" ? formatToLongDate(text) : "-",
      filters: sortFilterData(getUniqueValues("receipt_date_time")),
      onFilter: (value: any, record: any) =>
        filterByDate(value, record.receipt_date_time),
      sorter: (a: any, b: any) => sortByDate(a, b, "receipt_date_time", true),
    },
    {
      title: "Last Updated By",
      key: "lastUpdatedBy",
      dataIndex: "updated_by",
      render: (text: any) => wrappedText(text, 30),
      sorter: (a: any, b: any) => sortByUpdatedBy(a, b, "updated_by"),
      filters: getUniqueValues("updated_by"),
      onFilter: (value: any, record: any) => record.updated_by === value,
    },
    {
      title: "Last Updated Date",
      key: "lastUpdatedDate",
      dataIndex: "updated_when",
      defaultSortOrder: "descend" as SortOrder,
      render: (text: any) =>
        text !== null && text !== "" ? formatToLongDate(text) : "-",
      filters: sortFilterData(getUniqueValues("updated_when")),
      onFilter: (value: any, record: any) =>
        filterByDate(value, record.updated_when),
      sorter: (a: any, b: any) => sortByDate(a, b, "updated_when", true),
    },
    {
      title: "Description",
      key: "description",
      dataIndex: "description",
      render: (text: any) => wrappedText(text, 30),
      sorter: (a: any, b: any) => a.description.localeCompare(b.description),
      filters: getUniqueValues("description"),
      onFilter: (value: any, record: any) => record.description === value,
    },
    {
      title: "Package Name",
      key: "packageName",
      dataIndex: "package_name",
      render: (text: any) => wrappedText(text, 30),
      sorter: (a: any, b: any) =>
        (a.package_name || "").toString().localeCompare((b.package_name || "").toString()),
      filters: getUniqueValues("package_name"),
      onFilter: (value: any, record: any) => record.package_name === value,
    },
    {
      title: "Registration ID",
      key: "registrationID",
      dataIndex: "eval_reg_number",
      render: (text: any) => wrappedText(text, 30),
      sorter: (a: any, b: any) => a.eval_reg_number.localeCompare(b.eval_reg_number),
      filters: getUniqueValues("eval_reg_number"),
      onFilter: (value: any, record: any) => record.eval_reg_number === value,
    },
  ];

  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map((col) => col.key),
  );
  const [tempVisibleColumns, setTempVisibleColumns] = useState(visibleColumns);

  const filteredColumns = allColumns.filter((col) =>
    visibleColumns.includes(col.key),
  );
  const handleColumnChange = (key: string) => {
    setTempVisibleColumns((prev) =>
      prev.includes(key) ? prev.filter((col) => col !== key) : [...prev, key],
    );
  };

  const handleOk = () => {
    setVisibleColumns(tempVisibleColumns);
    setDropdownVisible(false);
  };

  const handleReset = () => {
    setTempVisibleColumns(allColumns.map((col) => col.key));
  };

  const handleFilterChange = (key: string, value: any) => {
    onFilterChange(key, value, data, filters, setFilters, setFilteredData);
  };

  const handleResetFilters = () => {
    form.resetFields();
    setFilters({});
    setFilteredData(data);
  };

  const getUniqueOptions = (key: string) => {
    const uniqueValues = Array.from(
      new Set(filteredData.map((item: any) => item[key])),
    ).sort((a, b) => a?.toString().localeCompare(b?.toString()));

    return uniqueValues.map((value) => ({ label: value, value }));
  };

  const handleRowSelect = (
    selectedRowKeys: React.Key[],
    selectedRows: tableDataType[],
  ) => {
    setSelectedData(selectedRows);
  };

  const handleDownloadEvaluationReport = () => {
    setOpenZipDownloadWarning(false);
    const registrationIds: (string | undefined)[] = dataForExport.map(item => item?.eval_reg_number);
    downloadPackageHistoryReport(registrationIds).then((response) => {
      if(registrationIds.length === 1) {
        downloadFileUsingPreSignedURL(response?.data?.downloadUrl);
      } else {
        const zipBase64 = response?.data?.zipFile;
        downloadFileUsingBase64(zipBase64);
      }
    }).catch((error) => {
      openNotification("error", "Failed to download transfer package level report");
    });
  };

    const transferIdOptions = getUniqueOptions("transfer_id");
    const supplierOptions = getUniqueOptions("supplier_org");
    const lastUpdatedByOptions = getUniqueOptions("updated_by");

    useEffect(() => {
      const tableData: tableDataType[] = selectedData.map(
        (obj: tableDataType) => {
          const filteredObj: tableDataType = {};
          filteredColumns.forEach(({ dataIndex: key }) => {
            if (key === "msd_compound") {
              filteredObj[key as keyof tableDataType] = (
                obj[key] as string
              )?.split("-")[0];
            } else {
              filteredObj[key as keyof tableDataType] = (obj as any)[key];
            }
          });
          return filteredObj;
        },
      );
      setDataForExport(tableData);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedData, visibleColumns]);

    const items = [
      {
        key: "1",
        label: (
          <button
            onClick={() =>
              generatePDF(
                filteredColumns,
                visibleColumns,
                dataForExport,
                jsPDF,
                autoTable,
              )
            }
            style={{
              border: "none",
              outline: "none",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            PDF
          </button>
        ),
      },
      {
        key: "2",
        label: (
          <button
            onClick={() => generateExcel(dataForExport, allColumns)}
            style={{
              border: "none",
              outline: "none",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            Excel
          </button>
        ),
      },
      {
        key: "3",
        label: (
          <CSVLink
            ref={csvLinkRef}
            onClick={() => {
              csvLinkRef.current.link.download = `package_history_${getCurrentDateTimeForFilename()}.csv`;
            }}
            data={mapKeysToColumnTitles(dataForExport, allColumns)}
          >
            <button
              style={{
                border: "none",
                outline: "none",
                background: "transparent",
                cursor: "pointer",
              }}
            >
              CSV
            </button>
          </CSVLink>
        ),
      },
    ];

    const menu = (
      <div>
        <Menu>
          {allColumns.map((col) => (
            <Menu.Item key={col.key}>
              <Checkbox
                checked={tempVisibleColumns.includes(col.key)}
                onChange={() => handleColumnChange(col.key)}
                disabled={
                  tempVisibleColumns.length === 1 &&
                  tempVisibleColumns.includes(col.key)
                }
              >
                {col.title}
              </Checkbox>
            </Menu.Item>
          ))}
          <Space style={{ padding: "10px", textAlign: "right" }}>
            <Button size="small" onClick={handleReset} style={{ width: "70px" }}>
              Reset
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={handleOk}
              style={{ width: "70px" }}
            >
              OK
            </Button>
          </Space>
        </Menu>
      </div>
    );

    useEffect(() => {
      const tableData: tableDataType[] = selectedData.map((obj: tableDataType) => {
        const filteredObj: tableDataType = {};
        filteredColumns.forEach(({ dataIndex: key }) => {
          if (key === "msd_compound") {
            filteredObj[key as keyof tableDataType] = (obj[key] as string)?.split("-")[0];
          } else if (key === "package_name") {
            filteredObj[key as keyof tableDataType] = (obj[key] as string)?.[0];
          } else if (key === "receipt_date_time" || key === "updated_when") {
            filteredObj[key as keyof tableDataType] = formatToLongDate((obj[key] as string)?.split(" ")[0]);
          } else {
            filteredObj[key as keyof tableDataType] = (obj as any)[key];
          }
        });
        return filteredObj;
      });
      sortDataOnDate(tableData);
      setDataForExport(tableData);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedData, visibleColumns]);

    return (
      <div>
        <Heading title="Package History" />
        <div
          style={{
            margin: "20px 60px",
            backgroundColor: "white",
            padding: "10px",
          }}
        >
          <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Compound" name="compound">
                  <Input
                    onChange={(e) =>
                      handleFilterChange("msd_compound", e.target.value)
                    }
                    placeholder="Input"
                    allowClear
                    style={{ borderRadius: 0 }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Protocol Number" name="protocolNumber">
                  <Input
                    onChange={(e) =>
                      handleFilterChange("protocol_number", e.target.value)
                    }
                    placeholder="Input"
                    allowClear
                    style={{ borderRadius: 0 }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Transfer ID" name="transferID">
                  <Select
                    options={transferIdOptions}
                    onChange={(value) => handleFilterChange("transfer_id", value)}
                    allowClear
                    placeholder="Select"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Supplier" name="supplier">
                  <Select
                    options={supplierOptions}
                    onChange={(value) =>
                      handleFilterChange("supplier_org", value)
                    }
                    allowClear
                    placeholder="Select"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Receipt Date" name="receiptDate">
                  <DatePicker
                    style={{ width: "100%", borderRadius: 0 }}
                    onChange={(date, dateString) =>
                      handleFilterChange("receipt_date_time", dateString)
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={8}></Col>
              <Col span={8}>
                <Form.Item label="Last Updated By" name="lastUpdatedBy">
                  <Select
                    options={lastUpdatedByOptions}
                    onChange={(value) => handleFilterChange("updated_by", value)}
                    allowClear
                    placeholder="Select"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Last Updated Date" name="lastUpdatedDate">
                  <DatePicker
                    style={{ width: "100%", borderRadius: 0 }}
                    onChange={(date, dateString) =>
                      handleFilterChange("updated_when", dateString)
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={8}></Col>
              <Col span={16}>
                <Form.Item
                  label="Keyword Search"
                  name="search"
                  labelCol={{ span: 4 }}
                  wrapperCol={{ span: 20 }}
                >
                  <Input
                    onChange={(e) => handleFilterChange("search", e.target.value)}
                    allowClear
                    style={{ borderRadius: 0 }}
                    placeholder="Search"
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <div style={{ float: "right" }}>
                  <Button onClick={handleResetFilters}>Reset</Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
        <div
          style={{
            margin: "20px 60px",
            backgroundColor: "white",
            padding: "10px",
          }}
        >
          <div style={{ marginBottom: "20px", padding: "10px" }}>
            <Text className="TextColor">Package History</Text>
            <Space style={{ float: "right" }}>
              <Button
              disabled={!dataForExport.length}
              onClick={() => {
                const rowSelectedForDownload = dataForExport.length;
                if (rowSelectedForDownload > 1) {
                  setOpenZipDownloadWarning(true);
                  return;
                }
                handleDownloadEvaluationReport();
              }}>
                Download Transfer Package Level Report
              </Button>
              <Dropdown
                menu={{ items }}
                arrow
                disabled={!dataForExport.length || !visibleColumns.length}
              >
                <Button icon={<DownOutlined />} iconPosition="end">
                  Export
                </Button>
              </Dropdown>
              <Dropdown
                overlay={menu}
                visible={dropdownVisible}
                onVisibleChange={(flag) => setDropdownVisible(flag)}
                trigger={["click"]}
                placement="bottomLeft"
                arrow
              >
                <Button color="cyan" variant="solid">
                  Show/Hide Columns
                </Button>
              </Dropdown>
            </Space>
          </div>
          <Table
            rowSelection={{
              type: "checkbox",
              onChange: handleRowSelect,
            }}
            className="custom-table"
            rowKey={(record) => record.eval_reg_number ?? ""}
            columns={filteredColumns}
            dataSource={filteredData}
            tableLayout="fixed"
            size="small"
            loading={dataLoading}
            pagination={{
              current: tableRows.current,
              pageSize: tableRows.pageSize,
              showSizeChanger: true,
              pageSizeOptions: ["6", "10", "20", "30", "40"],
              showTotal: (total, range) => `Total ${total} items`,
            }}
            onChange={(rows) => {
              setTableRows({
                current: rows.current ?? 1,
                pageSize: rows.pageSize ?? 6,
              });
            }}
          />
        </div>
        <GenericDialog
          open={openZipDownloadWarning}
          submitButtonText="Download Transfer Package Level Report"
          cancelButtonText="Cancel"
          handleSubmit={handleDownloadEvaluationReport}
          handleCancel={() => setOpenZipDownloadWarning(false)}
          width={450}
          title="Download Transfer Package Level Reports"
          description="You’ve selected multiple packages. Their transfer package level reports will be combined and downloaded as a single .zip file."
          titleIcon={
            <InfoCircleOutlined
              style={{ color: "orange", marginRight: "15px" }}
            />
          }
        />
        {contextHolder}
      </div>
    );
  };

export default PackageHistory;
